import { useEffect, useState } from 'react';
import { api } from '../api.js';

export default function ExecutiveKpi({ onGoToDqa }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filterCat, setFilterCat] = useState('All');

  async function load() {
    setLoading(true);
    setErr('');
    try {
      const res = await api.getExecutiveKpis();
      setData(res);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const getTrafficBadge = (status) => {
    switch (status) {
      case 'GREEN':
        return <span className="cbadge proc" style={{ background: '#059669', color: '#fff' }}>🟢 On Target</span>;
      case 'AMBER':
        return <span className="cbadge" style={{ background: '#d97706', color: '#fff' }}>🟡 Attention</span>;
      case 'RED':
        return <span className="cbadge" style={{ background: '#dc2626', color: '#fff' }}>🔴 Under Target</span>;
      case 'CRITICAL':
        return <span className="cbadge" style={{ background: '#7f1d1d', color: '#fecaca', fontWeight: 700 }}>🚨 ZERO TOLERANCE</span>;
      default:
        return <span className="cbadge lt">{status}</span>;
    }
  };

  if (loading && !data) {
    return <div className="card"><p className="muted">Loading Executive Quality & Operations Dashboard…</p></div>;
  }

  if (err && !data) {
    return (
      <div className="card">
        <h2>Executive Quality & Operations Dashboard</h2>
        <p className="err">{err}</p>
        <button className="pri sm" onClick={load}>Retry</button>
      </div>
    );
  }

  const measures = data?.headlineMeasures || [];
  const categories = ['All', ...new Set(measures.map((m) => m.category))];
  const filtered = filterCat === 'All' ? measures : measures.filter((m) => m.category === filterCat);

  const greenCount = measures.filter((m) => m.trafficLight === 'GREEN').length;
  const criticalCount = measures.filter((m) => m.trafficLight === 'CRITICAL').length;
  const amberCount = measures.filter((m) => m.trafficLight === 'AMBER').length;
  const redCount = measures.filter((m) => m.trafficLight === 'RED').length;

  return (
    <div className="card" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>📊</span> Executive Quality & Operations Dashboard
          </h2>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Official 10 Headline Measures · Malaysian MOH Quality Standard &amp; Traffic-Light Governance
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onGoToDqa && (
            <button className="ghost sm" onClick={onGoToDqa}>
              🛡️ WHO DQA Guardian →
            </button>
          )}
          <button className="ghost sm" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : '🔄 Refresh Live'}
          </button>
        </div>
      </div>

      {/* Traffic Light Overview Strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div style={{ background: 'rgba(5, 150, 105, 0.1)', border: '1px solid rgba(5, 150, 105, 0.3)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ color: '#34d399', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>🟢 Green (Compliant)</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#10b981' }}>{greenCount} <span style={{ fontSize: '0.9rem', fontWeight: 400 }}>measures</span></div>
        </div>
        <div style={{ background: 'rgba(217, 119, 6, 0.1)', border: '1px solid rgba(217, 119, 6, 0.3)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ color: '#fbbf24', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>🟡 Amber (Monitoring)</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f59e0b' }}>{amberCount} <span style={{ fontSize: '0.9rem', fontWeight: 400 }}>measures</span></div>
        </div>
        <div style={{ background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ color: '#f87171', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>🔴 Red (Action Required)</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#ef4444' }}>{redCount} <span style={{ fontSize: '0.9rem', fontWeight: 400 }}>measures</span></div>
        </div>
        <div style={{ background: 'rgba(127, 29, 29, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ color: '#fca5a5', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>🚨 Zero-Tolerance Breaches</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: criticalCount > 0 ? '#ef4444' : '#10b981' }}>
            {criticalCount} <span style={{ fontSize: '0.9rem', fontWeight: 400 }}>breaches</span>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        {categories.map((c) => (
          <button
            key={c}
            className={'tab' + (filterCat === c ? ' on' : '')}
            onClick={() => setFilterCat(c)}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Measures Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {filtered.map((m) => (
          <div
            key={m.id}
            style={{
              background: '#040d1a',
              border: m.trafficLight === 'CRITICAL' ? '2px solid #ef4444' : '1px solid rgba(56, 189, 248, 0.2)',
              borderRadius: 8,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: m.trafficLight === 'CRITICAL' ? '0 0 16px rgba(239, 68, 68, 0.2)' : 'none'
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {m.category}
                </span>
                {getTrafficBadge(m.trafficLight)}
              </div>
              <h4 style={{ margin: '0 0 10px', fontSize: '1.05rem', color: '#f8fafc', fontWeight: 600 }}>
                {m.title}
              </h4>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: '2.2rem', fontWeight: 800, color: '#38bdf8' }}>
                  {m.value}
                </span>
                <span style={{ fontSize: '0.95rem', color: '#94a3b8' }}>
                  {m.unit}
                </span>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#64748b', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8 }}>
                Target Benchmark: <strong style={{ color: '#cbd5e1' }}>{m.target}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Clinical Quality Assurance Memo */}
      <div style={{ marginTop: 24, padding: 16, background: 'rgba(56, 189, 248, 0.05)', borderRadius: 8, border: '1px solid rgba(56, 189, 248, 0.2)' }}>
        <h4 style={{ margin: '0 0 8px', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>🛡️</span> Continuous Quality Improvement (CQI) Policy
        </h4>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8', lineHeight: 1.6 }}>
          All indicators are audited against Malaysian MOH Clinical Practice Guidelines (CPG) and Home Nursing Standards.
          Any red or zero-tolerance alert automatically triggers an instant supervisory review, root cause analysis (RCA),
          and an incident log entry into the D1 Quality Audit Trail.
        </p>
      </div>
    </div>
  );
}
